import { Box, CircularProgress } from "@mui/material";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CompleteIcon from "../../assets/Icons/CompleteIcon";
import InCompleteIcon from "../../assets/Icons/InCompleteIcon";
import {
  chefOfferingFieldNames,
  publicProfileFieldNames,
} from "../../constants/ProfileConstant";
import {
  DesktopProfilePageTab,
  ProfilePageAccordion,
  ProfilePageTab,
} from "../../constants/ProfilePage";
import useMediaQuery from "../../customHooks/useMediaQuery";
import { createUserLog } from "../../firebase/logs";
import getProfileCompletenessValue from "../../helper/getProfileCompletenessValue";
import User from "../../interfaces/User";
import {
  setActiveTab,
  setDesktopActiveTab,
  setExpandedAccordion,
} from "../../redux/createProfilePageSlice";
import { RootState } from "../../redux/store";
import { STRIPE_ACCOUNT_STATUS } from "../../types/enums";
import { fetchPostJSON } from "../../utils/api-helpers";
import Typography from "../Typography";

interface Props {
  setOpenModal?: Dispatch<SetStateAction<boolean>>;
  afterOnClick?: () => void;
}

const ProfileCompletenessChart = ({ setOpenModal, afterOnClick }: Props) => {
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const isMdSize = useMediaQuery("(min-width:900px)");

  const user = useSelector((state: RootState) => state.user.user);
  const { value: completenessValue, data: completenessData } =
    getProfileCompletenessValue(user);
  const userData = useSelector((state: RootState) => state.user.user);
  const router = useRouter();
  const dispatch = useDispatch();
  const hostPath = useSelector((state: RootState) => state.app.hostPath);
  const redirectUrl = `${hostPath}/profile/${userData.uid}`;
  const redirectToStripPage = async () => {
    try {
      const response = await fetchPostJSON("/api/users/create-account", {
        user: {
          uid: userData.uid,
          ...userData,
        },
        redirectUrl,
      });
      router.replace(response.data.redirectLink);
    } catch (err: any) {
      await createUserLog(
        userData.uid!,
        "Error: User Redirect to Stripe",
        err.message,
        { error: err.message },
        "Stripe Redirect Error"
      );
    }
  };

  const handleClick = async (fieldName: keyof User) => {
    // MOBILE: Handles the case when user is on mobile
    if (
      publicProfileFieldNames.includes(fieldName) ||
      chefOfferingFieldNames.includes(fieldName) ||
      [
        "profileImage",
        "imagesCount",
        "foodPreferences",
        "proofOfInsurance",
        "serveSafe",
        "contractSigned",
        "stripe_account_status",
        "address",
      ].includes(fieldName)
    ) {
      router.pathname !== "/profile/[uid]" &&
        router.push(`/profile/${userData.uid}`);
    }

    if (publicProfileFieldNames.includes(fieldName)) {
      dispatch(setActiveTab(ProfilePageTab.Information));
      dispatch(setExpandedAccordion(ProfilePageAccordion.PublicProfile));
    } else if (chefOfferingFieldNames.includes(fieldName)) {
      dispatch(setActiveTab(ProfilePageTab.Information));
      dispatch(setExpandedAccordion(ProfilePageAccordion.PublicChefOffering));
    } else if (fieldName === "imagesCount") {
      dispatch(setActiveTab(ProfilePageTab.Photo));
    } else if (fieldName === "foodPreferences") {
      dispatch(setActiveTab(ProfilePageTab.Information));
      dispatch(setExpandedAccordion(ProfilePageAccordion.FoodPreferences));
    } else if (["proofOfInsurance", "serveSafe"].includes(fieldName)) {
      dispatch(setActiveTab(ProfilePageTab.Information));
      dispatch(
        setExpandedAccordion(
          ProfilePageAccordion.PrivateAccountVerificationInformation
        )
      );
    } else if (fieldName === "contractSigned") {
      dispatch(setActiveTab(ProfilePageTab.Information));
      dispatch(setExpandedAccordion(ProfilePageAccordion.ContractSign));
      setTimeout(() => {
        !isMdSize && window.scrollTo(0, 800);
      }, 500);
    }
    // COMMON
    else if (
      fieldName === "stripe_account_status" &&
      userData.stripe_account_status === STRIPE_ACCOUNT_STATUS.IN_PROGRESS
    ) {
      if (
        userData.stripe_verification_attempted_at &&
        new Date(userData.stripe_verification_attempted_at) <
          new Date(Date.now() - 1000 * 60 * 10)
      ) {
        // router.pathname !== "/profile/[uid]" &&
        //   router.push(`/profile/${userData.uid}`);
        // dispatch(setDesktopActiveTab(DesktopProfilePageTab.Verification));
        // setIsRedirectingToStripe(true);
        // await redirectToStripPage();
        // setIsRedirectingToStripe(false);
      } else {
        // toast.info("We are verifying your account. Please try after some time");
        // return;
      }
    } else if (
      fieldName === "stripe_account_status" &&
      userData.stripe_account_status !== STRIPE_ACCOUNT_STATUS.SUCCESSFUL
    ) {
      // router.pathname !== "/profile/[uid]" &&
      //   router.push(`/profile/${userData.uid}`);
      // dispatch(setDesktopActiveTab(DesktopProfilePageTab.Verification));
      // setIsRedirectingToStripe(true);
      // await redirectToStripPage();
      // setIsRedirectingToStripe(false);
    }

    // DESKTOP: Handles the case when user is on desktop
    if (fieldName === "imagesCount") {
      dispatch(setDesktopActiveTab(DesktopProfilePageTab.Photo));
    } else if (
      [
        "proofOfInsurance",
        "serveSafe",
        "contractSigned",
        "stripe_account_status",
      ].includes(fieldName)
    ) {
      dispatch(setDesktopActiveTab(DesktopProfilePageTab.Verification));
    } else if (["email", "phoneNumber", "dob"].includes(fieldName)) {
      dispatch(setDesktopActiveTab(DesktopProfilePageTab.Private));
    } else {
      dispatch(setDesktopActiveTab(DesktopProfilePageTab.Public));
    }

    setOpenModal && setOpenModal(false);
    afterOnClick && afterOnClick();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
        }}
      >
        {completenessData.map((completenessDataItem, index) => (
          <Box
            onClick={() => handleClick(completenessDataItem.fieldName)}
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
            }}
          >
            <Box sx={{ width: "17px", height: "17px" }}>
              {completenessDataItem.isCompleted ? (
                <CompleteIcon />
              ) : (
                <InCompleteIcon />
              )}
            </Box>
            {isRedirectingToStripe &&
            completenessDataItem.fieldName === "stripe_account_status" ? (
              <CircularProgress
                size={20}
                thickness={4}
                sx={{ ml: 2, my: "2px" }}
              />
            ) : (
              <Typography variant="body2">
                {completenessDataItem.label}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default ProfileCompletenessChart;
